import React, { useState, useEffect, useRef } from 'react';
import { adminApi } from '../../api/admin';
import { Users, RefreshCw, ChevronRight, ChevronDown, CreditCard, Loader, AlertCircle, CheckCircle, Pencil, X } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';

const AdminSettingsPlayers = () => {
  const { locale } = useLanguage();
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null); // { team_id, team, league }
  const [players, setPlayers] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [expandedLeagues, setExpandedLeagues] = useState(new Set()); // leghe aperte (di default tutte chiuse)
  const [syncChanges, setSyncChanges] = useState([]); // cambiamenti ultima sync (da response o da GET sync-changes)
  const [syncRunAt, setSyncRunAt] = useState(null); // ISO string ultima run (per mostrare il blocco "dopo che la sync è stata chiamata")
  const [loadingSyncChanges, setLoadingSyncChanges] = useState(false);
  const [editPlayer, setEditPlayer] = useState(null); // { id, full_name, league, team, team_id } – modal edit league/team
  const [editForm, setEditForm] = useState({ league: '', team: '', team_id: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const syncPollRef = useRef(null);
  const syncPollTimeoutRef = useRef(null);

  const loadTeams = async () => {
    setLoadingTeams(true);
    try {
      const res = await adminApi.getSettingsPlayersTeams();
      const list = res?.teams ?? res?.data?.teams ?? [];
      if (Array.isArray(list) && list.length >= 0) {
        setTeams(list);
        if (selectedTeam && !list.some(t => t.team_id === selectedTeam.team_id && (t.team || '') === (selectedTeam.team || '') && (t.league || '') === (selectedTeam.league || ''))) {
          setSelectedTeam(null);
          setPlayers([]);
        }
      }
    } catch (err) {
      console.error('Load teams error:', err);
      setMessage({ type: 'error', text: err?.data?.error || err?.response?.data?.error || 'Failed to load teams' });
    } finally {
      setLoadingTeams(false);
    }
  };

  const loadPlayers = async (teamObj) => {
    if (!teamObj) return;
    setLoadingPlayers(true);
    setPlayers([]);
    try {
      const hasId = teamObj.team_id != null && teamObj.team_id !== '';
      const res = hasId
        ? await adminApi.getSettingsPlayersByTeam(teamObj.team_id)
        : await adminApi.getSettingsPlayersByTeam('null', { team: teamObj.team || '', league: teamObj.league || '' });
      const list = res?.players ?? res?.data?.players ?? [];
      if (Array.isArray(list)) setPlayers(list);
    } catch (err) {
      console.error('Load players error:', err);
      setMessage({ type: 'error', text: err?.data?.error || err?.response?.data?.error || 'Failed to load players' });
    } finally {
      setLoadingPlayers(false);
    }
  };

  useEffect(() => {
    loadTeams();
  }, []);

  const loadLastSyncChanges = async () => {
    setLoadingSyncChanges(true);
    try {
      const res = await adminApi.getSettingsPlayersSyncChanges();
      const data = res ?? res?.data;
      const list = data?.changes ?? [];
      const at = data?.created_at ?? null;
      setSyncChanges(Array.isArray(list) ? list : []);
      setSyncRunAt(at ? (typeof at === 'string' ? at : new Date(at).toISOString()) : null);
    } catch {
      setSyncChanges([]);
      setSyncRunAt(null);
    } finally {
      setLoadingSyncChanges(false);
    }
  };

  useEffect(() => {
    loadLastSyncChanges();
  }, []);

  useEffect(() => {
    if (selectedTeam) {
      loadPlayers(selectedTeam);
    } else {
      setPlayers([]);
    }
  }, [selectedTeam]);

  const stopSyncPolling = () => {
    if (syncPollRef.current) {
      clearInterval(syncPollRef.current);
      syncPollRef.current = null;
    }
    if (syncPollTimeoutRef.current) {
      clearTimeout(syncPollTimeoutRef.current);
      syncPollTimeoutRef.current = null;
    }
  };

  const applySyncResult = (data) => {
    if (data?.success) {
      setMessage({
        type: 'success',
        text: `Sync completata: ${data.processed ?? 0} processati, ${data.updated ?? 0} aggiornati, ${data.skipped ?? 0} saltati, ${data.errors ?? 0} errori.`
      });
      setSyncChanges(Array.isArray(data.changes) ? data.changes : []);
      setSyncRunAt(new Date().toISOString());
      loadTeams();
      if (selectedTeam) loadPlayers(selectedTeam);
    } else {
      setMessage({ type: 'error', text: data?.error || data?.message || 'Sync fallita' });
    }
  };

  const handleSyncLeagueTeam = async () => {
    setSyncing(true);
    setMessage({ type: '', text: '' });
    stopSyncPolling();
    try {
      const res = await adminApi.syncPlayersLeagueTeam();
      const data = res ?? res?.data;
      if (!data?.success) {
        setMessage({ type: 'error', text: data?.error || 'Sync failed' });
        setSyncing(false);
        return;
      }
      if (data.started) {
        setMessage({ type: 'success', text: 'Sync avviata in background. Attendo completamento…' });
        const pollMs = 5000;
        const maxPollMs = 25 * 60 * 1000;
        const doPoll = async () => {
          try {
            const statusRes = await adminApi.getSyncLeagueTeamStatus();
            const statusData = statusRes ?? statusRes?.data;
            if (statusData?.status === 'idle' && statusData?.result != null) {
              stopSyncPolling();
              setSyncing(false);
              applySyncResult(statusData.result);
            }
          } catch {
            // ignore poll errors, retry next tick
          }
        };
        doPoll(); // primo poll subito
        syncPollRef.current = setInterval(doPoll, pollMs);
        syncPollTimeoutRef.current = setTimeout(() => {
          stopSyncPolling();
          setSyncing(false);
          setMessage({ type: 'success', text: 'Sync avviata. Se non è ancora completata, ricarica la pagina tra qualche minuto per vedere i cambiamenti.' });
        }, maxPollMs);
      } else {
        if (data.processed != null || data.updated != null) {
          applySyncResult(data);
        } else {
          setMessage({ type: 'success', text: data?.message || 'Sync avviata.' });
        }
        setSyncing(false);
      }
    } catch (err) {
      const msg = err?.data?.error || err?.response?.data?.error || err?.message || 'Sync failed';
      setMessage({ type: 'error', text: msg });
      setSyncing(false);
    }
  };

  useEffect(() => {
    return () => { stopSyncPolling(); };
  }, []);

  const isSelected = (t) => selectedTeam && selectedTeam.team_id === t.team_id && (selectedTeam.team || '') === (t.team || '') && (selectedTeam.league || '') === (t.league || '');

  // Raggruppa team per lega (chiave: league, valore: array di team)
  const teamsByLeague = React.useMemo(() => {
    const map = {};
    (teams || []).forEach((t) => {
      const key = (t.league != null && String(t.league).trim()) ? String(t.league).trim() : '—';
      if (!map[key]) map[key] = [];
      map[key].push(t);
    });
    return map;
  }, [teams]);
  const leagueOrder = Object.keys(teamsByLeague).sort((a, b) => (a === '—' ? 1 : b === '—' ? -1 : a.localeCompare(b)));

  const toggleLeague = (leagueName) => {
    setExpandedLeagues((prev) => {
      const next = new Set(prev);
      if (next.has(leagueName)) next.delete(leagueName);
      else next.add(leagueName);
      return next;
    });
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 8000);
  };

  const openEditPlayer = (p) => {
    setEditPlayer(p);
    setEditForm({
      league: p.league ?? '',
      team: p.team ?? '',
      team_id: p.team_id != null ? String(p.team_id) : '',
    });
  };

  const closeEditPlayer = () => {
    setEditPlayer(null);
    setSavingEdit(false);
  };

  const handleSaveEditPlayer = async () => {
    if (!editPlayer) return;
    setSavingEdit(true);
    setMessage({ type: '', text: '' });
    try {
      const payload = {};
      if (editForm.league !== (editPlayer.league ?? '')) payload.league = editForm.league;
      if (editForm.team !== (editPlayer.team ?? '')) payload.team = editForm.team;
      const tidStr = (editForm.team_id ?? '').toString().trim();
      const tid = tidStr === '' ? null : (parseInt(tidStr, 10) || null);
      if (tid !== (editPlayer.team_id ?? null)) payload.team_id = tid;
      if (Object.keys(payload).length === 0) {
        setMessage({ type: 'error', text: 'Nessuna modifica.' });
        setSavingEdit(false);
        return;
      }
      await adminApi.updateSettingsPlayerLeagueTeam(editPlayer.id, payload);
      setMessage({ type: 'success', text: `Giocatore ${editPlayer.full_name} aggiornato.` });
      closeEditPlayer();
      loadTeams();
      if (selectedTeam) loadPlayers(selectedTeam);
    } catch (err) {
      const msg = err?.data?.error || err?.response?.data?.error || err?.message || 'Salvataggio fallito';
      setMessage({ type: 'error', text: msg });
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold flex items-center">
          <Users className="w-8 h-8 mr-3 text-purple-400" />
          Players
        </h1>
        <button
          onClick={handleSyncLeagueTeam}
          disabled={syncing}
          className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {syncing ? (
            <>
              <Loader className="w-4 h-4 mr-2 animate-spin" />
              Syncing...
            </>
          ) : (
            <>
              <RefreshCw className="w-4 h-4 mr-2" />
              Sync league/team from Sportmonks
            </>
          )}
        </button>
      </div>

      {syncing && (
        <div className="mb-4 p-4 rounded-lg border bg-amber-900/30 border-amber-600 text-amber-200 flex items-center">
          <Loader className="w-5 h-5 mr-2 animate-spin flex-shrink-0" />
          <span>Sync league/team in corso… Attendere (può richiedere diversi minuti).</span>
        </div>
      )}

      {message.text && (
        <div className={`mb-4 p-4 rounded-lg border ${
          message.type === 'success' ? 'bg-green-900/50 border-green-700 text-green-300' :
          message.type === 'error' ? 'bg-red-900/50 border-red-700 text-red-300' :
          'bg-blue-900/50 border-blue-700 text-blue-300'
        }`}>
          <div className="flex items-center">
            {message.type === 'success' ? <CheckCircle className="w-5 h-5 mr-2" /> : <AlertCircle className="w-5 h-5 mr-2" />}
            {message.text}
          </div>
        </div>
      )}

      <p className="text-gray-400 text-sm mb-6">
        Lista team e relativi giocatori. Per ogni team puoi vedere quanti giocatori hanno card associate.
        Usa &quot;Sync league/team from Sportmonks&quot; per allineare team e lega dei giocatori con i dati Sportmonks (sovrascrive lo stesso record).
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Teams list */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
          <h2 className="text-lg font-semibold mb-3">Team</h2>
          {loadingTeams ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : teams.length === 0 ? (
            <p className="text-gray-400">Nessun team con player_count.</p>
          ) : (
            <div className="space-y-1 max-h-[400px] overflow-y-auto">
              {leagueOrder.map((leagueName) => {
                const isOpen = expandedLeagues.has(leagueName);
                const leagueTeams = teamsByLeague[leagueName] || [];
                return (
                  <div key={leagueName}>
                    <button
                      type="button"
                      onClick={() => toggleLeague(leagueName)}
                      className="w-full flex items-center gap-2 text-xs font-semibold text-purple-300 uppercase tracking-wider px-2 py-2 rounded sticky top-0 bg-gray-800 z-10 hover:bg-gray-700/80 text-left"
                    >
                      {isOpen ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                      <span>{leagueName}</span>
                      <span className="text-gray-500 font-normal normal-case ml-1">({leagueTeams.length})</span>
                    </button>
                    {isOpen && (
                      <ul className="space-y-1 mt-0.5 pl-1">
                        {leagueTeams.map((t, idx) => (
                          <li key={t.team_id != null ? `tid-${t.team_id}` : `t-${idx}-${(t.team || '')}-${(t.league || '')}`}>
                            <button
                              type="button"
                              onClick={() => setSelectedTeam(isSelected(t) ? null : t)}
                              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                                isSelected(t) ? 'bg-purple-600/30 text-white' : 'hover:bg-gray-700 text-gray-200'
                              }`}
                            >
                              <span className="font-medium truncate">{t.team || `Team ${t.team_id ?? '-'}`}</span>
                              <span className="text-sm text-gray-400 ml-2 shrink-0">
                                {t.player_count} players · {t.total_copies ?? 0} copie
                              </span>
                              <ChevronRight className={`w-4 h-4 shrink-0 ${isSelected(t) ? 'rotate-90' : ''}`} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Players of selected team */}
        <div className="bg-gray-800 rounded-lg border border-gray-700 p-4">
          <h2 className="text-lg font-semibold mb-3">
            {selectedTeam
              ? (selectedTeam.team || `Team ${selectedTeam.team_id ?? '-'}`) + ' – Giocatori'
              : 'Seleziona un team'}
          </h2>
          {!selectedTeam ? (
            <p className="text-gray-400">Clicca su un team per vedere i giocatori.</p>
          ) : loadingPlayers ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-8 h-8 animate-spin text-purple-400" />
            </div>
          ) : players.length === 0 ? (
            <p className="text-gray-400">Nessun giocatore per questo team.</p>
          ) : (
            <ul className="space-y-1 max-h-[400px] overflow-y-auto">
              {players.map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-gray-900/50 group">
                  <div className="truncate min-w-0">
                    <span className="font-medium text-white">{p.full_name}</span>
                    {p.is_out_of_dios10 ? (
                      <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded bg-red-900/40 border border-red-700 text-red-300 text-xs">
                        OUT OF DIOS
                      </span>
                    ) : null}
                    {p.position && <span className="text-gray-400 text-sm ml-2">({p.position})</span>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {(p.copy_count ?? p.card_count ?? 0) > 0 ? (
                      <span className="flex items-center text-emerald-400 text-sm">
                        <CreditCard className="w-4 h-4 mr-1" />
                        {p.copy_count ?? p.card_count} copia{(p.copy_count ?? p.card_count) !== 1 ? 'e' : ''}
                      </span>
                    ) : (
                      <span className="text-gray-500 text-sm">Nessuna copia</span>
                    )}
                    <button
                      type="button"
                      onClick={() => openEditPlayer(p)}
                      className="p-1.5 rounded text-gray-400 hover:text-purple-400 hover:bg-gray-700 transition-colors"
                      title="Modifica league/team"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Cambiamenti dopo la sync Sportmonks */}
      <div className="mt-6 bg-gray-800 rounded-lg border border-gray-700 p-4">
        <h2 className="text-lg font-semibold mb-3">Cambiamenti sync Sportmonks</h2>
        {loadingSyncChanges ? (
          <div className="flex items-center justify-center py-6">
            <Loader className="w-6 h-6 animate-spin text-purple-400" />
          </div>
        ) : syncRunAt ? (
          <>
            <p className="text-gray-400 text-sm mb-3">
              Ultima sync: {new Date(syncRunAt).toLocaleString(locale || 'it-IT')}. Di seguito i record aggiornati (team / lega).
            </p>
            {syncChanges.length === 0 ? (
              <p className="text-gray-400">Nessun cambiamento nell&apos;ultima sync.</p>
            ) : (
              <div className="overflow-x-auto max-h-[320px] overflow-y-auto">
                <table className="w-full text-sm text-left">
                  <thead className="text-gray-400 uppercase tracking-wider sticky top-0 bg-gray-800 border-b border-gray-600">
                    <tr>
                      <th className="py-2 pr-4">Giocatore</th>
                      <th className="py-2 pr-4">Team (prima → dopo)</th>
                      <th className="py-2 pr-4">Lega (prima → dopo)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncChanges.map((c, idx) => (
                      <tr key={c.player_id != null ? c.player_id : idx} className="border-b border-gray-700/50 hover:bg-gray-700/30">
                        <td className="py-2 pr-4 font-medium text-white">{c.full_name ?? `ID ${c.player_id}`}</td>
                        <td className="py-2 pr-4 text-gray-300">
                          {(c.old_team ?? '—')} → {(c.new_team ?? '—')}
                          {(c.old_team_id !== c.new_team_id) && (
                            <span className="text-gray-500 ml-1">(id {c.old_team_id ?? '?'} → {c.new_team_id ?? '?'})</span>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-gray-300">{(c.old_league ?? '—')} → {(c.new_league ?? '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <p className="text-gray-400">Esegui la sync per vedere qui i cambiamenti (team/lega) applicati ai giocatori.</p>
        )}
      </div>

      {/* Modal modifica league/team giocatore */}
      {editPlayer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60" onClick={closeEditPlayer}>
          <div
            className="bg-gray-800 border border-gray-600 rounded-xl shadow-xl w-full max-w-md p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Modifica league/team</h3>
              <button type="button" onClick={closeEditPlayer} className="p-1 rounded text-gray-400 hover:text-white hover:bg-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-400 text-sm mb-4">{editPlayer.full_name}</p>
            <div className="space-y-3 mb-5">
              {(() => {
                const leagueOptions = leagueOrder.includes(editForm.league || '—')
                  ? leagueOrder
                  : [editForm.league || '—'].filter(Boolean).concat(leagueOrder);
                const teamsInLeague = (teamsByLeague[editForm.league] || []).slice();
                const currentMatch = teamsInLeague.some(
                  (t) => (t.team || '') === (editForm.team || '') && (t.team_id == null ? editForm.team_id == null || editForm.team_id === '' : Number(t.team_id) === Number(editForm.team_id))
                );
                if (editForm.league && !currentMatch && (editForm.team || editForm.team_id !== undefined && editForm.team_id !== '')) {
                  teamsInLeague.unshift({ team_id: editForm.team_id || null, team: editForm.team || '', league: editForm.league });
                }
                const selectedTeamIndex = teamsInLeague.findIndex(
                  (t) => (t.team || '') === (editForm.team || '') && (t.team_id == null ? (editForm.team_id == null || editForm.team_id === '') : Number(t.team_id) === Number(editForm.team_id))
                );
                return (
                  <>
                    <label className="block text-sm font-medium text-gray-300">
                      Lega
                      <select
                        value={editForm.league}
                        onChange={(e) => {
                          const league = e.target.value;
                          const inLeague = teamsByLeague[league] || [];
                          const first = inLeague[0];
                          setEditForm((f) => ({
                            ...f,
                            league,
                            team: first ? (first.team || '') : '',
                            team_id: first ? (first.team_id ?? '') : '',
                          }));
                        }}
                        className="mt-1 w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">— Seleziona lega —</option>
                        {leagueOptions.map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                      </select>
                    </label>
                    <label className="block text-sm font-medium text-gray-300">
                      Team
                      <select
                        value={selectedTeamIndex >= 0 ? selectedTeamIndex : ''}
                        onChange={(e) => {
                          const idx = parseInt(e.target.value, 10);
                          if (Number.isNaN(idx) || idx < 0) return;
                          const t = teamsInLeague[idx];
                          if (t) setEditForm((f) => ({ ...f, team: t.team || '', team_id: t.team_id != null ? t.team_id : '' }));
                        }}
                        className="mt-1 w-full px-3 py-2 bg-gray-900 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="">— Seleziona team —</option>
                        {teamsInLeague.map((t, idx) => (
                          <option key={t.team_id != null ? `tid-${t.team_id}` : `t-${idx}-${t.team}-${t.league}`} value={idx}>
                            {t.team || `Team ${t.team_id ?? '-'}`}
                          </option>
                        ))}
                      </select>
                    </label>
                    <p className="text-gray-500 text-xs">Team ID (Sportmonks) viene impostato automaticamente dal team scelto.</p>
                  </>
                );
              })()}
            </div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeEditPlayer}
                className="px-4 py-2 rounded-lg border border-gray-600 text-gray-300 hover:bg-gray-700"
              >
                Annulla
              </button>
              <button
                type="button"
                onClick={handleSaveEditPlayer}
                disabled={savingEdit}
                className="px-4 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 flex items-center"
              >
                {savingEdit ? <Loader className="w-4 h-4 mr-2 animate-spin" /> : null}
                Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSettingsPlayers;
